import { splitLinks } from './links.ts';
import { parseMessage } from './smileys.ts';

/**
 * Where a selection inside one message begins and ends, counted in the stored
 * body — the string with `:wink:` still spelled out — and not in the letters on
 * screen.
 *
 * That is the whole reason this file exists. A bubble is not text: the codec
 * turns a code into an `<img>` and an address into an `<a>`, so the browser's
 * own selection would hand back the visible characters and a copied fragment
 * would arrive at the other end with its smileys silently missing. Everything
 * here counts in the body instead, and the rendered atoms carry their place in
 * it (`data-at`) so a caret landing anywhere on screen can be turned back into
 * an index.
 */
export interface Span {
  start: number;
  end: number;
}

/**
 * One rendered thing, and where in the body it came from.
 *
 * The list mirrors what TextBody draws, in the order it draws it — addresses
 * found first and the codes parsed inside what is left, so an address that
 * happens to contain something code-shaped stays whole. Both splitters slice
 * without losing a character, which is what makes the running offset below the
 * truth rather than an estimate.
 */
export type Atom =
  | { type: 'text'; at: number; value: string }
  | { type: 'link'; at: number; href: string; value: string }
  | { type: 'smiley'; at: number; slug: string; label: string };

/**
 * Word characters for the grab. A smiley code is a word by being one atom.
 *
 * The hyphen is one of them: «44-108» is the invoice number the handoff draws
 * somebody selecting, and «19-го» is one word in Russian rather than two.
 */
const WORD = /[\p{L}\p{N}_-]/u;

/** Whether an index sits between two word characters rather than on an edge. */
function insideWord(body: string, index: number): boolean {
  return index > 0 && index < body.length && WORD.test(body[index - 1] ?? '') && WORD.test(body[index] ?? '');
}

export function atomsOf(body: string, links = true): Atom[] {
  const atoms: Atom[] = [];
  const runs = links ? splitLinks(body) : [{ type: 'text' as const, value: body }];
  let at = 0;

  for (const run of runs) {
    if (run.type === 'link') {
      atoms.push({ type: 'link', at, href: run.href, value: run.value });
      at += run.value.length;
      continue;
    }
    for (const part of parseMessage(run.value)) {
      if (part.type === 'text') {
        atoms.push({ type: 'text', at, value: part.value });
        at += part.value.length;
      } else {
        // parseMessage matches on the exact slug (BY_SLUG is a plain map), so
        // the code it consumed was `:slug:` and nothing else — which is what
        // lets its source length be worked out here rather than carried around.
        atoms.push({ type: 'smiley', at, slug: part.slug, label: part.label });
        at += part.slug.length + 2;
      }
    }
  }

  return atoms;
}

/** How many characters of the body an atom stands for: `:wink:` is six, not one. */
export function atomLength(atom: Atom): number {
  return atom.type === 'smiley' ? atom.slug.length + 2 : atom.value.length;
}

/** The atom an index falls in, or the last one for an index at the very end. */
function atomAt(atoms: Atom[], index: number): Atom | null {
  for (const atom of atoms) {
    if (index >= atom.at && index < atom.at + atomLength(atom)) return atom;
  }
  return atoms.length > 0 ? (atoms[atoms.length - 1] ?? null) : null;
}

/** A span in the order a reader would write it, whichever way the finger went. */
export function spanOf(anchor: number, focus: number): Span {
  return anchor <= focus ? { start: anchor, end: focus } : { start: focus, end: anchor };
}

export function isEmpty(span: Span): boolean {
  return span.end <= span.start;
}

/** «Всё» — the bubble, from its first character to its last. */
export function wholeSpan(body: string): Span {
  return { start: 0, end: body.length };
}

/**
 * Pushes both ends out of any atom they landed inside.
 *
 * A smiley and an address are single things on screen; half of `:wink:` in a
 * quote is the literal `:wi` where a picture belongs, the same failure quoteOf
 * guards against on the way out. Text is left exactly where it is — «в 12:30:15»
 * is code-shaped and still ordinary characters somebody may want three of.
 */
export function snapToAtoms(body: string, span: Span, links = true): Span {
  let { start, end } = span;

  for (const atom of atomsOf(body, links)) {
    if (atom.type === 'text') continue;
    const from = atom.at;
    const to = atom.at + atomLength(atom);
    if (start > from && start < to) start = from;
    if (end > from && end < to) end = to;
  }

  return { start: Math.max(0, start), end: Math.min(body.length, end) };
}

/**
 * What a press-and-hold grabs: the word under the finger.
 *
 * A smiley or an address is grabbed whole — it is one thing on screen and the
 * indices inside it were never shown. A finger between two words takes the one
 * that just ended, the same way a caret past the last letter of a word belongs
 * to it; a lone space grabbed on its own would look like the gesture had failed.
 */
export function wordSpanAt(body: string, index: number, links = true): Span {
  const atoms = atomsOf(body, links);
  const atom = atomAt(atoms, Math.max(0, Math.min(body.length, index)));
  if (!atom) return { start: 0, end: 0 };
  if (atom.type !== 'text') return { start: atom.at, end: atom.at + atomLength(atom) };

  const local = Math.max(0, Math.min(atom.value.length - 1, index - atom.at));
  const at = WORD.test(atom.value[local] ?? '')
    ? local
    : // A caret sitting just past the last letter of a word belongs to that
      // word: it is where a tap at the end of one lands.
      local > 0 && WORD.test(atom.value[local - 1] ?? '')
      ? local - 1
      : -1;
  if (at < 0) return { start: atom.at + local, end: atom.at + local + 1 };

  let from = at;
  while (from > 0 && WORD.test(atom.value[from - 1] ?? '')) from -= 1;
  let to = at + 1;
  while (to < atom.value.length && WORD.test(atom.value[to] ?? '')) to += 1;
  return { start: atom.at + from, end: atom.at + to };
}

/**
 * Both ends out to whole words — the granularity a handle starts dragging in.
 *
 * Only an end standing *inside* a word moves. One already between two words is
 * where the reader put it, and rounding it outwards would make a handle
 * impossible to park.
 */
export function expandToWords(body: string, span: Span, links = true): Span {
  const start = insideWord(body, span.start) ? wordSpanAt(body, span.start, links).start : span.start;
  const end = insideWord(body, span.end) ? wordSpanAt(body, span.end, links).end : span.end;
  return snapToAtoms(body, { start, end }, links);
}

/**
 * The selection a finger dragging away from the word it grabbed should have.
 *
 * The grabbed word is the floor: dragging back over it does not eat into it,
 * and dragging past either edge grows that side only. Which is what makes the
 * gesture reversible — a drag that overshoots can be walked back to the word it
 * started from rather than having to be started again.
 */
export function growSpan(body: string, anchor: Span, index: number, links = true): Span {
  if (index > anchor.end) return expandToWords(body, { start: anchor.start, end: index }, links);
  if (index < anchor.start) return expandToWords(body, { start: index, end: anchor.end }, links);
  return anchor;
}

/** The fragment itself, in body form: codes, not pictures. */
export function selectedText(body: string, span: Span): string {
  return body.slice(Math.max(0, span.start), Math.min(body.length, span.end));
}

/**
 * How much is selected, as the reader would count it: a smiley is one character,
 * because one is what they are looking at. This is the number the panel says out
 * loud, so it has to agree with the screen rather than with the column.
 */
export function selectedLength(body: string, span: Span, links = true): number {
  let count = 0;
  for (const atom of atomsOf(body, links)) {
    const from = Math.max(span.start, atom.at);
    const to = Math.min(span.end, atom.at + atomLength(atom));
    if (to <= from) continue;
    count += atom.type === 'smiley' ? 1 : to - from;
  }
  return count;
}

/**
 * Cuts one text atom into its selected and unselected pieces, so the bubble can
 * draw the highlight itself. The same shape splitHighlight returns, and for the
 * same reason: the run is already in TextBody's hands, and marking it there is
 * cheaper than a second pass over the DOM.
 */
export function splitSelection(
  value: string,
  at: number,
  span: Span,
): { value: string; on: boolean }[] {
  const from = Math.max(0, Math.min(value.length, span.start - at));
  const to = Math.max(0, Math.min(value.length, span.end - at));
  if (to <= from) return [{ value, on: false }];

  const pieces: { value: string; on: boolean }[] = [];
  if (from > 0) pieces.push({ value: value.slice(0, from), on: false });
  pieces.push({ value: value.slice(from, to), on: true });
  if (to < value.length) pieces.push({ value: value.slice(to), on: false });
  return pieces;
}
